//TODO: this file won't exist eventually, no point migrating to TS

import * as React from "react";
import ResizeObserver from "resize-observer-polyfill";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {
    isObject,
    isFunction,
    bindAll,
    isArray,
    isEmpty,
    omit,
    noop
} from "underscore";
import { ThemeProvider } from "styled-components";
import mainTheme from "style/theme";
import Backbone from "pages/common/lib/backbone-helpers";
import BackboneForm from "@grabyo/backbone-forms";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useDrag } from "react-dnd";
import { isServer } from "util/browser-utils";

/**
 * @method isStatelessComponent
 *
 * @description
 * This function here is used to check whether a React Component is a Functional Component or a Class Component.
 * Either Babel or Webpack compilation compiles the React Component to old ES5 functional Constructors.
 *
 * As a result, isFunction, from underscore, is treating the compiled component as a Function, even when it is not a Functional Component.
 * A Functional Component will never have a render method as it will always return you the JSX.
 * A Class Component has a render method which is part of the object prototype.
 * Therefore, this function checks whether the compiled Component has the render method or not inside of it's prototype to determine whether
 *  or not it is a Functional or Class based Component (Stateless vs State).
 */

const isStatelessComponent = (Component) => !Component.prototype.render;

export const connectToBackboneModel = (WrappedComponent) => {
    return class BackboneModelComponent extends React.Component {
        forceUpdate() {
            super.forceUpdate();
        }

        componentDidMount() {
            this.props.model.on(`change`, this.forceUpdate, this);
        }

        componentWillUnmount() {
            this.props.model.off(`change`, this.forceUpdate, this);
        }

        componentDidUpdate(prevProps) {
            if (this.props.model !== prevProps.model) {
                prevProps.model.off(`change`, this.forceUpdate, this);
                this.props.model.on(`change`, this.forceUpdate, this);
            }
        }

        render() {
            return (
                <WrappedComponent
                    {...omit(this.props, `model`)}
                    {...this.props.model.toJSON()}
                />
            );
        }
    };
};

export const connectToBackboneCollection = (WrappedComponent) => {
    return class BackboneCollectionComponent extends React.Component {
        forceUpdate() {
            super.forceUpdate();
        }

        componentDidMount() {
            this.props.collection.on(
                `update change sync reset`,
                this.forceUpdate,
                this
            );
        }

        componentDidUpdate(prevProps) {
            if (this.props.collection !== prevProps.collection) {
                prevProps.collection.off(
                    `update change sync`,
                    this.forceUpdate,
                    this
                );
                this.props.collection.on(
                    `update change sync`,
                    this.forceUpdate,
                    this
                );
            }
        }

        componentWillUnmount() {
            this.props.collection.off(
                `update change sync reset`,
                this.forceUpdate,
                this
            );
        }

        render() {
            return (
                <WrappedComponent
                    {...omit(this.props, `collection`)}
                    items={this.props.collection.toJSON()}
                />
            );
        }
    };
};

/**
 * Wrap a Backbone View in a React Component
 *
 * Don't use this. You should refactor the View to React instead.
 *
 * props:
 * @Events prop must be an object of shape { eventName: [callbacks] }
 */

export const backboneViewAsReactComponent = (view) => {
    class ViewAsComponent extends React.Component {
        constructor(props) {
            super(props);
            this.state = {
                isRendered: false
            };

            this.wrapperRef = React.createRef();
            this.view = new view(props);

            if (props.events) {
                this.registerEvents(props.events);
            }

            this.dispatchEvents = this.dispatchEvents.bind(this);

            bindAll(this, `renderView`);
        }

        render() {
            return (
                <span
                    className="js-backbone-wrapper"
                    ref={this.wrapperRef}
                ></span>
            );
        }

        componentDidMount() {
            this.renderView(this.props, { shouldCallSetFieldValue: false });
            this.props.onReady && this.props.onReady(this.view);
        }

        componentWillUnmount() {
            this.view.remove();
        }

        renderView(props, options) {
            this.wrapperRef.current.appendChild(
                this.view.render(props, options).el
            );
        }

        registerEvents(events = {}) {
            for (const key of Object.keys(events)) {
                for (const callback of events[key]) {
                    this.view.on(key, callback, this.view);
                }
            }
        }

        dispatchEvents(eventName, ...eventArgs) {
            if (!this.props.events) {
                return;
            }
            const { events } = this.props;
            if (!Object.prototype.hasOwnProperty.call(events, eventName)) {
                return;
            }

            events[eventName].forEach((cb) => cb.call(this.view, ...eventArgs));
        }
    }

    ViewAsComponent.propTypes = {
        events: (props) => {
            if (!Object.prototype.hasOwnProperty.call(props, `events`)) {
                return;
            }

            if (!isObject(props.events)) {
                return new Error(`events prop must be an object`);
            }

            const keys = Object.keys(props.events);

            for (const key of keys) {
                if (!isArray(props.events[key])) {
                    return new Error(`events prop keys must be arrays`);
                }

                if (
                    props.events[key].filter((cb) => !isFunction(cb)).length > 0
                ) {
                    return new Error(
                        `events props keys must be arrays of callback functions`
                    );
                }
            }
        }
    };

    return ViewAsComponent;
};

/**
 * @summary Appends a react component as a direct child of an element
 * without wrapping the component
 * @param {React.Component} component The react component
 * @param {Element} el The target HTML element
 * @param {Function} cb Callback function
 */
export const appendToElement = (component, el, cb) => {
    const wrapper = document.createElement(`div`);
    render(component, wrapper, () => {
        el.appendChild(wrapper.firstChild);
        if (isFunction(cb)) {
            cb();
        }
    });
};

export const DOMAppend = (component, el, cb, style) => {
    const wrapper = document.createElement(`div`);
    wrapper.classList.add(`${style ? style : `js-react-wrapper`}`);
    render(component, wrapper, () => {
        el.appendChild(wrapper);
        if (isFunction(cb)) {
            cb();
        }
    });
    return wrapper;
};

export const DOMPrepend = (component, el, cb) => {
    const wrapper = document.createElement(`div`);
    wrapper.classList.add(`js-react-wrapper`);

    render(component, wrapper, () => {
        el.insertBefore(wrapper, el.firstChild);
        if (isFunction(cb)) {
            cb();
        }
    });

    return wrapper;
};

export const isReactComponent = (object) =>
    isFunction(object) &&
    object.prototype &&
    isObject(object.prototype.isReactComponent);

export const render = (component, el, cb) => {
    ReactDOM.render(
        <ThemeProvider theme={mainTheme}>{component}</ThemeProvider>,
        el,
        () => isFunction(cb) && cb()
    );
};

export const DOMRender = render;

export const removeReactFromNode = (container, callback) => {
    if (document.body.contains(container)) {
        Array.from(container.childNodes).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const unmounted = ReactDOM.unmountComponentAtNode(node);
                if (unmounted) {
                    // Cleaning the mess added by DOMAppend
                    const wrapper = node.parentNode;
                    const childNode = wrapper.childNodes[0];
                    if (
                        childNode.classList.contains(`js-react-wrapper`) &&
                        childNode.childElementCount === 0
                    ) {
                        wrapper.removeChild(childNode);
                    }

                    if (isFunction(callback)) {
                        callback(node);
                    }
                } else {
                    removeReactFromNode(node);
                }
            }
        });
    }
};

/**
 * @function
 * @summary A simple function that returns a backbone view that renders the passed React component
 * @param {(props) => JSX.Element} Component The component you want the view to render
 * @param {Object} initialProps Initial props to be set on a view when it first renders
 * @returns {Backbone.View} A nice Backbone view 👴
 */
export const ReactComponentAsBackboneView = (
    Component,
    initialProps = {},
    elementAttributes = {}
) => {
    const BackboneView = Backbone.View.extend({
        initialize(options = {}) {
            this.props = {
                ...initialProps,
                ...options
            };
        },
        updateProps(props) {
            if (!isObject(props)) {
                throw new TypeError(`setProps not being passed valid props`);
            }
            this.props = { ...this.props, ...props };
            this.render();
        },
        setProps(props) {
            if (!isObject(props)) {
                throw new TypeError(`setProps not being passed valid props`);
            }
            this.props = props;
            this.render();
        },
        destroy() {
            ReactDOM.unmountComponentAtNode(this.el);
            BackboneView.__super__.destroy.call(this, arguments);
        },
        render() {
            // Added an extra value to refer to the specific component instance for Backbone.
            if (isStatelessComponent(Component)) {
                render(<Component {...this.props} />, this.el);
            } else {
                this.componentInstance = React.createRef();
                render(
                    <Component ref={this.componentInstance} {...this.props} />,
                    this.el
                );
            }
            Object.keys(elementAttributes).forEach((key) => {
                this.el.setAttribute(key, elementAttributes[key]);
            });
            return this;
        }
    });
    return BackboneView;
};

export class BackboneView extends React.Component {
    static propTypes = {
        View: PropTypes.func.isRequired,
        onReady: PropTypes.func,
        ssr: PropTypes.func,
        options: PropTypes.object
    };

    static defaultProps = {
        onReady: noop,
        ssr: () => `Loading...`,
        options: {}
    };

    constructor(props) {
        super(props);
        this.ref = React.createRef();
    }

    shouldComponentUpdate(nextProps, nextState) {
        const shouldUpdate =
            this.props.shouldUpdate &&
            this.props.shouldUpdate({
                nextProps,
                nextState,
                prevProps: this.props,
                prevState: this.state
            });
        shouldUpdate && this.reset(nextProps);

        return false;
    }

    reset(props) {
        // Avoid re-rendering the React component, just manipulate the backbone component
        this.props = props;
        if (this.view && isFunction(this.view.destroy)) {
            if (this.view.el.parentElement) {
                const parentEl = this.view.el.parentElement;
                parentEl.removeChild(this.view.el);
                this.view.destroy();
                this.view = new this.props.View(this.props.options);

                parentEl.appendChild(this.view.render().el);
                this.props.onReady(this.view);
            }
        }
    }

    componentDidMount() {
        if (!isServer()) {
            this.view = new this.props.View(this.props.options);
            this.ref.current.replaceWith(this.view.render().el);
            this.props.onReady(this.view);
        }
    }

    destroyView() {
        if (this.view && isFunction(this.view.destroy)) {
            if (this.view.el.parentElement) {
                this.view.el.parentElement.removeChild(this.view.el);
            }
            this.view.destroy();
        }
    }

    componentWillUnmount() {
        this.destroyView();
        delete this.view;
    }

    render() {
        return <span ref={this.ref}>{isServer() && this.props.ssr()}</span>;
    }
}

export const AppContext = React.createContext({});

export const ModalContext = React.createContext({ isNestedInModal: false });

export const ReactInputAsBackboneEditor = (InputComponent, alertTemplate) => {
    const InputToEditor = BackboneForm.editors.Base.extend({
        initialize(options) {
            this.props = options.schema;
            this.name = options.key;
            this.ref = React.createRef();
            if (alertTemplate) {
                this.alertTemplate = alertTemplate;
            }
            InputToEditor.__super__.initialize.call(this, options);
            this.listenTo(this.model, `change`, this.render);
            bindAll(this, `onChange`, `onBlur`, `onFocus`);
        },
        render() {
            render(
                <InputComponent
                    {...this.props}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    onChange={this.onChange}
                    value={this.value}
                    viewRef={this.ref}
                />,
                this.el
            );
            return this;
        },
        enable() {
            this.disabled = false;
            this.render();
        },
        disable() {
            this.disabled = true;
            this.render();
        },
        blur() {
            this.ref.blur();
        },
        focus() {
            this.ref.focus();
        },
        getValue() {
            return this.value;
        },
        setValue(value) {
            this.value = value;
            this.render();
        },
        setDisabled(value) {
            this.disabled = value;
            this.render();
        },
        onChange(e) {
            this.value = e;
            this.trigger(`change`, this);
            this.render();
        },
        setInfoTooltip(message) {
            this.infoTooltip = message;
            this.render();
        },
        onBlur() {
            this.trigger(`blur`, this);
        },
        onFocus() {
            this.trigger(`focus`, this);
        }
    });

    return InputToEditor;
};

export const useOnResize = (ref, updateFn) => {
    React.useEffect(() => {
        const ro = new ResizeObserver((mutations) => {
            mutations.forEach(
                ({ contentRect }) => ref.current && updateFn(contentRect)
            );
        });
        ro.observe(ref.current);
        return () => {
            ro.disconnect();
        };
    }, []);
};

/**
 * @typedef {DragDefinition}
 * @property {Function<MouseEvent} onDrag
 * @property {Function<MouseEvent} onDragStart
 * @property {Function<MouseEvent} onDragEnd
 */

/**
 * @function
 * @summary Allows components to hook into a number of common drag callbacks
 * @param {React.Ref} ref el that drag will occur on
 * @param {DragDefinition} obj callbacks for drag operation. Mouse event
 */
export const useOnDrag = (
    ref,
    { onDrag: drag, onDragEnd: end, onDragStart: start }
) => {
    // This bullsh*t is necessary to solve the React hook stale closures problem:
    // https://dmitripavlutin.com/react-hooks-stale-closures/
    // https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
    //
    // It is the React recommended approach to dealing with this issue:
    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
    // This was too stressful for what this effect does
    const stateRef = React.useRef();
    stateRef.current = {};
    stateRef.current.onDrag = drag;
    stateRef.current.onDragEnd = end;
    stateRef.current.onDragStart = start;

    React.useEffect(() => {
        const onDrag = (e) => stateRef.current.onDrag(e);
        const onDragEnd = (e) => stateRef.current.onDragEnd(e);
        const onDragStart = (e) => stateRef.current.onDragStart(e);

        const onMouseUp = (e) => {
            onDragEnd(e);
            document.body.removeEventListener(`mousemove`, onDrag);
            document.body.removeEventListener(`mouseup`, onMouseUp);
        };

        ref.current.addEventListener(`mousedown`, (e) => {
            onDragStart(e);
            document.body.addEventListener(`mousemove`, onDrag);
            document.body.addEventListener(`mouseup`, onMouseUp);
        });
        return () => {
            document.body.removeEventListener(`mousemove`, onDrag);
            document.body.removeEventListener(`mouseup`, onMouseUp);
        };
    }, []);
};

export const useDragWithoutPreview = (args) => {
    const params = useDrag(args);
    React.useEffect(() => {
        // params[2] is preview function
        params[2](getEmptyImage(), { captureDraggingState: true });
    }, []);
    return params;
};
